import * as React from 'react';
import get from 'lodash.get';
import { Box, FlexBox, Typography, AbsoluteBox, Grid, MuiBox } from '@kis-technologies/kis-ui';
import { ComponentProps } from '../types';
import { selectors as configSelectors, Configuration } from 'services/configuration';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';
import { selectors as hardwareSelectors, actions as hardwareActions, Scanner } from 'services/hardware';
import { selectors as kioskSelectors, KioskHardware } from 'services/kiosk';
import BarcodeScan from 'assets/barcode_scan.png';
import { ActionOverlay } from 'components/ActionOverlay';
import { ERROR, USER_ERROR, NOT_CONNECTED, OPERATIONAL } from 'constants/hardware';
import { Footer } from 'components/Footer';
import { useStyles } from '../styled';
import { useTranslation } from 'react-i18next';

export const MembershipLookup = (props: ComponentProps) => {
  const { onNext, onPrev, loading: ordersLoading } = props;
  const { scannerRead, resetScanner } = useActions(hardwareActions);
  const { status, loading }: Scanner = useSelector(hardwareSelectors.selectScanner);
  const kiosk = useSelector(kioskSelectors.selectKiosk);
  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const fontFamily = get(config, 'configuration.globalSettings.fonts.primary');
  const messaging = get(config, 'willCall.scanMembershipCard.messages');
  const classes = useStyles({ fontFamily })();
  const { t, i18n } = useTranslation();

  React.useEffect(() => {
    if (!loading && !status.message) {
      scannerRead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getBarcodeScanImgUrl = () => {
    if (kiosk) {
      const barcodeScanner = kiosk.hardware.find((device: KioskHardware) => device.type === 'barcodeScanner');
      if (barcodeScanner && barcodeScanner.imageUrl && barcodeScanner.imageUrl.primary) {
        return barcodeScanner.imageUrl.primary;
      }
    }
    return undefined;
  };

  if (status.message && status.code === OPERATIONAL && !ordersLoading) {
    onNext({ memberBarcode: status.message });
    resetScanner();
  }

  return (
    <>
      {[ERROR, USER_ERROR, NOT_CONNECTED].includes(status.code) && !loading && !ordersLoading && (
        <ActionOverlay
          title={
            <Box margin="0 0 5" width={710}>
              <Typography fontSize={64} fontWeight={600} align="center" sx={{ lineHeight: 1 }}>
                {t('commonText.somethingWentWrong')}
              </Typography>
            </Box>
          }
          confirmText={t('commonText.tryAgain')}
          onConfirm={scannerRead}
          cancelText={t('commonText.back')}
          onCancel={() => {
            resetScanner();
            onPrev();
          }}
        />
      )}

      <Grid className={classes.membershipContainer} container>
        <Grid item lg={12} xl={6} className={classes.scanTextContainer}>
          <FlexBox margin="0 0 8" direction="column">
            <Typography fontSize={100} fontWeight={600} sx={{ lineHeight: 1.1 }}>
              {get(messaging, `header.${i18n.language}`) || t('membershipScan.scanYourCard')}
            </Typography>
          </FlexBox>
          <MuiBox>
            <Typography className={classes.subHeading}>
              {get(messaging, `subheader.${i18n.language}`) || t('membershipScan.scanUsingBarcode')}
            </Typography>
          </MuiBox>
        </Grid>
        <Grid item lg={12} xl={6} className={classes.imageContainer}>
          <FlexBox fullHeight align="center" justify="center">
            {!getBarcodeScanImgUrl() && <img height={538} alt="url" src={BarcodeScan} />}
            {getBarcodeScanImgUrl() && <img width={500} height={538} alt="url" src={getBarcodeScanImgUrl()} />}
          </FlexBox>
        </Grid>
      </Grid>
      <AbsoluteBox bottom="0" height="90" left="0" fullWidth padding="6 11">
        <Footer
          prevText={t('commonText.back')}
          onPrevClick={() => {
            resetScanner();
            onPrev();
          }}
        />
      </AbsoluteBox>
    </>
  );
};
